import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";

export type WalletLogoSource = 'home_config' | 'tenant';
export type BackgroundFit = 'cover' | 'contain';

// Persisted in the `image_settings` JSONB column.
// Future: overlay color, blur, brightness, rotation, etc.
export interface ImageSettings {
  background?: {
    fit?: BackgroundFit;
    focal_x?: number; // 0..100
    focal_y?: number; // 0..100
  };
}

export interface HomeConfig {
  id?: number;
  name: string;
  background_img_url: string;
  image_settings?: ImageSettings;
  logo_url: string;
  weather_icon_color: string;
  weather_bg_color: string;
  featured_enabled: boolean;
  featured_title_en: string;
  featured_title_es: string;
  featured_max_items: number;
  weekly_enabled: boolean;
  weekly_title_en: string;
  weekly_title_es: string;
  weekly_max_items: number;
  weekly_article_type_id: number | null;
  is_active: boolean;
  // Which logo to use for the Apple Wallet pass: this home_config's logo_url
  // or the tenant.settings.logo. Defaults to 'home_config'.
  wallet_logo_source: WalletLogoSource;
}

interface HomeConfigListResponse {
  data: HomeConfig[];
}

interface EstateGroupItem {
  id: number;
  name: string;
  home_url: string;
  logo_url: string;
}

interface EstateGroupListResponse {
  data: EstateGroupItem[];
}

interface AdItem {
  id: number;
  title_en: string;
  title_es: string;
  art_img_url: string;
  short_description_en: string;
  short_description_es: string;
  background_color: string;
  priority: number;
  status: string;
}

interface AdListResponse {
  data: AdItem[];
}

interface ArticleItem {
  id: number;
  title_en: string;
  title_es: string;
  art_img_url?: string;
  status: string;
  article_type_id: number;
}

interface ArticleListResponse {
  data: ArticleItem[];
}

// Fetch the first active home config
export const useHomeConfig = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['homeConfig'],
    queryFn: async () => {
      const response = await defaultInstance<HomeConfigListResponse>({
        url: '/home_config?is_active=true&limit=1',
        method: 'GET',
      });
      const items = (response as unknown as HomeConfigListResponse).data || [];
      return items.length > 0 ? items[0] : null;
    },
  });

  return {
    config: data as HomeConfig | null | undefined,
    isLoading,
    error,
  };
};

export const useUpdateHomeConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<HomeConfig> }) => {
      return await defaultInstance({
        url: `/home_config/${id}`,
        method: 'PUT',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homeConfig'] });
      toaster.positive('Home config updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update home config', { autoHideDuration: 3000 });
    },
  });
};

export const useCreateHomeConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<HomeConfig>) => {
      return await defaultInstance({
        url: '/home_config',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homeConfig'] });
      toaster.positive('Home config created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create home config', { autoHideDuration: 3000 });
    },
  });
};

// Fetch estate groups for the preview selector
export const useEstateGroupsForHome = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['estateGroupsForHome'],
    queryFn: async () => {
      const response = await defaultInstance<EstateGroupListResponse>({
        url: '/estate_group?limit=100',
        method: 'GET',
      });
      return (response as unknown as EstateGroupListResponse).data || [];
    },
  });

  return {
    estateGroups: (data as EstateGroupItem[] | undefined) || [],
    isLoading,
    error,
  };
};

// Fetch published ads ordered by priority
export const usePublishedAds = (limit: number = 5) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['publishedAds', limit],
    queryFn: async () => {
      const response = await defaultInstance<AdListResponse>({
        url: `/ads?status=published&order_by=priority&order_direction=ASC&limit=${limit}`,
        method: 'GET',
      });
      return (response as unknown as AdListResponse).data || [];
    },
  });

  return {
    ads: (data as AdItem[] | undefined) || [],
    isLoading,
    error,
  };
};

// Fetch published articles
export const usePublishedArticles = (limit: number = 5) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['publishedArticles', limit],
    queryFn: async () => {
      const response = await defaultInstance<ArticleListResponse>({
        url: `/article?status=published&limit=${limit}`,
        method: 'GET',
      });
      return (response as unknown as ArticleListResponse).data || [];
    },
  });

  return {
    articles: (data as ArticleItem[] | undefined) || [],
    isLoading,
    error,
  };
};
